import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { AlertProvider, useAlert } from "../Components/AlertContext";
import { ConfirmationProvider, useConfirmation } from "../Components/ConfirmationContext";

import Breadcrumbs from "../Components/Breadcrumbs";
import Button from "../Components/Buttons";
import { CustomInput, CustomTextarea, CustomToggle, CustomSelect } from "../Components/CustomFormElements";
import { isEmpty } from "../utils/validationUtil";
import PageLoader from "../Components/PageLoader";

import { PostCallAnalysisApiProvider, usePostCallAnalysisApi } from "../Services/PostCallAnalysisApiProvider";
import { AgentApiProvider, useAgentApi } from "../Services/AgentApiProvider";
import { ChannelApiProvider, useChannelApi } from "../Services/ChannelApiProvider";

import "./PostCallAnalysisDetail.css";
import PostCallAnalyticsSummary from "../Components/PostCallAnalyticsSummary";

const METRIC_TYPE_OPTIONS = [
    { value: "text", label: "Text" },
    { value: "numeric", label: "Numeric" },
    { value: "boolean", label: "Boolean" },
    { value: "categorical", label: "Categorical" },
    { value: "list", label: "List" }
];

/**
 * Analytics period helper (same behavior as Organization page).
 */
const getDateRangeByPeriod = (period) => {
    const today = new Date();
    let startDate = null;

    switch (period) {
        case "all_time":
            startDate = new Date(2000, 0, 1);
            break;
        case "year":
            startDate = new Date();
            startDate.setFullYear(today.getFullYear() - 1);
            startDate.setHours(0, 0, 0, 0);
            break;
        case "month":
            startDate = new Date();
            startDate.setMonth(today.getMonth() - 1);
            startDate.setHours(0, 0, 0, 0);
            break;
        case "day":
            startDate = new Date();
            startDate.setDate(today.getDate());
            startDate.setHours(0, 0, 0, 0);
            break;
        default:
            startDate = null;
    }

    return {
        start_date: startDate ? startDate.toISOString() : null,
        end_date: today.toISOString()
    };
};

function PostCallAnalysisDetail({ canEdit = true }) {
    const { organizationId, configId } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();

    const {
        getConfigById,
        createConfig,
        updateConfig,
        getMetrics,
        createMetric,
        updateMetric,
        deleteMetric,
        setChannels,
        getAnalyticsSummary
    } = usePostCallAnalysisApi();

    const { getAgents } = useAgentApi();
    const { getChannels } = useChannelApi();

    const [loading, setLoading] = useState(false);
    const [channelsList, setChannelsList] = useState([]);
    const [isNew, setIsNew] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);

    // Config fields
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [threadFilter, setThreadFilter] = useState("");
    const [assistantKey, setAssistantKey] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [selectedChannelIds, setSelectedChannelIds] = useState([]);
    const [includeStandardMetrics, setIncludeStandardMetrics] = useState(true);

    // Metrics
    const [metrics, setMetrics] = useState([]);

    // New metric form
    const [showNewMetricForm, setShowNewMetricForm] = useState(false);
    const [newMetricName, setNewMetricName] = useState("");
    const [newMetricDisplayName, setNewMetricDisplayName] = useState("");
    const [newMetricType, setNewMetricType] = useState("text");
    const [newMetricDescription, setNewMetricDescription] = useState("");
    const [newMetricAllowedValues, setNewMetricAllowedValues] = useState([]);

    // Assistants list
    const [agents, setAgents] = useState([]);

    // ---- Post-call analytics summary (REAL REQUEST) ----
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summary, setSummary] = useState(null);

    const [selectedSummaryPeriod, setSelectedSummaryPeriod] = useState("day");
    const [customSummaryFrom, setCustomSummaryFrom] = useState("");
    const [customSummaryTo, setCustomSummaryTo] = useState("");

    const numericMetrics = useMemo(
        () => (metrics || []).filter((m) => m.metric_type === "numeric"),
        [metrics]
    );

    const categoricalMetrics = useMemo(
        () => (metrics || []).filter((m) => m.metric_type === "categorical"),
        [metrics]
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch agents
            const agentsResponse = await getAgents(0, 100);
            setAgents(agentsResponse.items || []);

            // Fetch channels
            const channelsResponse = await getChannels(0, 500);
            setChannelsList(channelsResponse.items || []);

            if (configId !== "new") {
                // Fetch config with details
                const config = await getConfigById(configId, true);
                console.log("conf", config);

                setName(config.name);
                setDescription(config.description || "");
                setThreadFilter(config.properties.thread_filter || "");
                setAssistantKey(config.assistant_key);
                setIsActive(config.is_active);
                setSelectedChannelIds(config.channel_ids || []);
                setMetrics(config.metrics || []);

                setIsNew(false);
                setIsDisabled(!canEdit);
            } else {
                setIsNew(true);
                setIsDisabled(!canEdit);
            }
        } catch (err) {
            console.error(err);
            showAlert("fail", "Failed to load data");
        } finally {
            setLoading(false);
        }
    }, [configId, canEdit, getConfigById, getAgents, getChannels, showAlert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSummaryPeriodChange = (e) => setSelectedSummaryPeriod(e.target.value);

    const fetchSummary = useCallback(async () => {
        if (configId === "new") return;

        // show empty state if nothing to track
        if ((numericMetrics.length === 0) && (categoricalMetrics.length === 0)) {
            setSummary(null);
            return;
        }

        let start_date, end_date;

        if (selectedSummaryPeriod === "custom") {
            if (!customSummaryFrom || !customSummaryTo) {
                // don't fetch until both dates are selected
                return;
            }
            start_date = customSummaryFrom;
            end_date = customSummaryTo;
        } else {
            ({ start_date, end_date } = getDateRangeByPeriod(selectedSummaryPeriod));
        }

        setSummaryLoading(true);
        try {
            const params = {};
            if (start_date) params.start_date = start_date;
            if (end_date) params.end_date = end_date;

            const data = await getAnalyticsSummary(configId, params);
            setSummary(data);
        } catch (err) {
            console.error(err);
            showAlert("fail", "Failed to load post-call analytics summary");
        } finally {
            setSummaryLoading(false);
        }
    }, [
        configId,
        selectedSummaryPeriod,
        customSummaryFrom,
        customSummaryTo,
        numericMetrics,
        categoricalMetrics,
        showAlert,
        getAnalyticsSummary
    ]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const onCancel = () => {
        navigate(`/organizations/${organizationId}/post-call-analysis`);
    };

    const onSave = async () => {
        if (isEmpty(name)) {
            showAlert("fail", "Please enter a name");
            return;
        }

        if (isEmpty(assistantKey)) {
            showAlert("fail", "Please select an assistant");
            return;
        }

        setLoading(true);
        try {
            if (isNew) {
                const data = {
                    name,
                    description,
                    thread_filter: threadFilter,
                    assistant_key: assistantKey,
                    channel_ids: selectedChannelIds,
                    include_standard_metrics: includeStandardMetrics
                };

                const newConfig = await createConfig(data);
                showAlert("success", "Configuration created!");

                navigate(`/organizations/${organizationId}/post-call-analysis/${newConfig.key}`);
            } else {
                const data = {
                    name,
                    description,
                    thread_filter: threadFilter,
                    assistant_key: assistantKey,
                    is_active: isActive,
                    channel_ids: selectedChannelIds
                };

                await updateConfig(configId, data);
                showAlert("success", "Configuration updated!");
            }
        } catch (err) {
            console.error(err);
            showAlert("fail", "Failed to save configuration");
        } finally {
            setLoading(false);
        }
    };

    const onAddMetric = async () => {
        if (isEmpty(newMetricName) || isEmpty(newMetricDisplayName)) {
            showAlert("fail", "Please enter metric name and display name");
            return;
        }

        setLoading(true);
        try {
            const metricData = {
                name: newMetricName,
                display_name: newMetricDisplayName,
                metric_type: newMetricType,
                description: newMetricDescription,
                constraints: (newMetricType === "categorical" || newMetricType === "list") && newMetricAllowedValues.length > 0
                    ? { values: newMetricAllowedValues }
                    : null
            };

            const newMetric = await createMetric(configId, metricData);
            setMetrics([...metrics, newMetric]);
            setShowNewMetricForm(false);
            setNewMetricName("");
            setNewMetricDisplayName("");
            setNewMetricType("text");
            setNewMetricDescription("");
            setNewMetricAllowedValues([]);
            showAlert("success", "Metric added!");
        } catch (err) {
            console.error(err);
            showAlert("fail", "Failed to add metric");
        } finally {
            setLoading(false);
        }
    };

    const onDeleteMetric = (metricKey, isStandard) => {
        if (isStandard) {
            showAlert("fail", "Cannot delete standard metrics");
            return;
        }

        showConfirmation(
            "Are you sure you want to delete this metric?",
            async () => {
                setLoading(true);
                try {
                    await deleteMetric(configId, metricKey);
                    setMetrics(metrics.filter(m => m.key !== metricKey));
                    showAlert("success", "Metric deleted!");
                } catch (err) {
                    console.error(err);
                    showAlert("fail", "Failed to delete metric");
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const onToggleMetricActive = async (metric) => {
        setLoading(true);
        try {
            await updateMetric(configId, metric.key, { is_active: !metric.is_active });
            setMetrics(metrics.map(m =>
                m.key === metric.key ? { ...m, is_active: !m.is_active } : m
            ));
            showAlert("success", "Metric updated!");
        } catch (err) {
            console.error(err);
            showAlert("fail", "Failed to update metric");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <PageLoader />;

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row">
                {/* Main Settings */}
                <div className="col-6 radius-yes p-2 bg-yes">
                    <h2 className="h2-title">
                        {isNew ? "Create Post-Call Analysis Configuration" : `Edit: ${name}`}
                    </h2>

                    <div className='input-block d-flex flex-row justify-between'>
                        <div className='input-description'>
                            <div className='input-title'>Configuration name</div>
                            <div className='small-font'>Enter configuration name</div>
                        </div>
                        <div className='align-wrapper d-flex align-center'>
                            <CustomInput
                                id="config-name"
                                label="Name"
                                placeholder="Enter name"
                                type="text"
                                value={name}
                                onChange={(id, value) => setName(value)}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>

                    <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                            <div className="input-title">Description</div>
                            <div className="small-font">Enter description</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                            <CustomTextarea
                                id="description"
                                label="Description"
                                name="description"
                                type="text"
                                addedText={description}
                                onChange={(value) => setDescription(value)}
                                resizable={false}
                                disabled={isDisabled}
                                height="100px"
                            />
                        </div>
                    </div>

                    <div className="input-block d-flex flex-row justify-between">
                        <div className="input-description">
                            <div className="input-title">Thread Filter</div>
                            <div className="small-font">Enter thread filter</div>
                        </div>
                        <div className="align-wrapper d-flex align-center">
                            <CustomTextarea
                                id="threadFilter"
                                label="Thread Filter"
                                name="threadFilter"
                                type="text"
                                addedText={threadFilter}
                                onChange={(value) => setThreadFilter(value)}
                                resizable={false}
                                disabled={isDisabled}
                                height="100px"
                            />
                        </div>
                    </div>

                    <div className='input-block d-flex flex-row justify-between'>
                        <div className='input-description'>
                            <div className='input-title'>Assistant</div>
                            <div className='small-font'>Select assistant for analysis</div>
                        </div>
                        <div className='align-wrapper d-flex align-center'>
                            <CustomSelect
                                id="assistant-select"
                                label="Assistant"
                                options={agents.map((a) => ({
                                    value: a.key,
                                    label: a.name,
                                }))}
                                value={assistantKey}
                                multiSelect={false}
                                onChange={(val) => setAssistantKey(val)}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>

                    <div className='input-block d-flex flex-row justify-between'>
                        <div className='input-description'>
                            <div className='input-title'>Channels</div>
                            <div className='small-font'>Select channels to analyze</div>
                        </div>
                        <div className='align-wrapper d-flex align-center'>
                            <CustomSelect
                                id="channels-select"
                                label="Channels"
                                options={channelsList.map((c) => ({
                                    value: c.id,
                                    label: c.name,
                                }))}
                                value={selectedChannelIds}
                                multiSelect={true}
                                onChange={(val) => setSelectedChannelIds(val)}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>

                    {isNew && (
                        <div className='input-row input-block d-flex flex-row justify-between'>
                            <div className='input-description'>
                                <div className='input-title'>Include standard metrics</div>
                                <div className='small-font'>Pre-populate with standard analysis metrics</div>
                            </div>
                            <div className='align-wrapper d-flex align-center'>
                                <CustomToggle
                                    id="toggle-standard-metrics"
                                    label={includeStandardMetrics ? 'Yes' : 'No'}
                                    checked={includeStandardMetrics}
                                    onChange={(id, value) => setIncludeStandardMetrics(value)}
                                    disabled={isDisabled}
                                />
                            </div>
                        </div>
                    )}

                    {!isNew && (
                        <div className='input-row input-block d-flex flex-row justify-between'>
                            <div className='input-description'>
                                <div className='input-title'>Active</div>
                                <div className='small-font'>Enable or disable this configuration</div>
                            </div>
                            <div className='align-wrapper d-flex align-center'>
                                <CustomToggle
                                    id="toggle-active"
                                    label={isActive ? 'Active' : 'Inactive'}
                                    checked={isActive}
                                    onChange={(id, value) => setIsActive(value)}
                                    disabled={isDisabled}
                                />
                            </div>
                        </div>
                    )}

                    <div className="buttons-container justify-end gap-12">
                        <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
                            Save
                        </Button>
                        <Button variant="danger" size="medium" onClick={onCancel}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>

            {/* Metrics Section - Only show for existing configs */}
            {!isNew && (
                <div className="row mt-2">
                    <div className="col-6 radius-yes p-2 bg-yes">
                        <h3 className="h3-title">Metrics</h3>
                        <div className='small-font mb-2'>Define the metrics to extract from conversations</div>

                        {metrics.map((metric) => (
                            <div key={metric.key} className='metric-row mb-2 p-2 border-yes radius-yes'>
                                <div className='d-flex flex-row justify-between align-center mb-1'>
                                    <div className='d-flex flex-column'>
                                        <div className='input-title'>{metric.display_name}</div>
                                        <div className='small-font'>
                                            {metric.is_standard ? 'Standard metric' : 'Custom metric'} • {metric.metric_type}
                                        </div>
                                    </div>
                                    <div className='d-flex align-center gap'>
                                        <CustomToggle
                                            id={`metric-active-${metric.key}`}
                                            label={metric.is_active ? 'Active' : 'Inactive'}
                                            checked={metric.is_active}
                                            onChange={() => onToggleMetricActive(metric)}
                                            disabled={isDisabled}
                                        />
                                        {!metric.is_standard && !isDisabled && (
                                            <Button
                                                variant='danger'
                                                size='small'
                                                onClick={() => onDeleteMetric(metric.key, metric.is_standard)}
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {metric.description && (
                                    <div className='metric-description-text small-font' style={{ color: '#666', marginTop: '4px' }}>
                                        {metric.description}
                                    </div>
                                )}

                                {metric.constraints?.values && metric.constraints.values.length > 0 && (
                                    <div className='metric-allowed-values small-font' style={{ color: '#666', marginTop: '4px' }}>
                                        <span style={{ fontWeight: 500 }}>Allowed values: </span>
                                        {metric.constraints.values.join(", ")}
                                    </div>
                                )}
                            </div>
                        ))}

                        {!showNewMetricForm ? (
                            !isDisabled && (
                                <Button
                                    variant='secondary'
                                    size='medium'
                                    onClick={() => setShowNewMetricForm(true)}
                                >
                                    <i className="fa-solid fa-plus"></i> Add Custom Metric
                                </Button>
                            )
                        ) : (
                            <div className="new-metric-form mt-2 p-2 border-yes radius-yes">
                                <h4 className="h4-title">New Custom Metric</h4>

                                <div className='input-block d-flex flex-row justify-between'>
                                    <div className='input-description'>
                                        <div className='input-title'>Metric name</div>
                                        <div className='small-font'>Internal name (no spaces)</div>
                                    </div>
                                    <div className='align-wrapper d-flex align-center'>
                                        <CustomInput
                                            id="new-metric-name"
                                            label="Name"
                                            placeholder="e.g., customer_effort_score"
                                            type="text"
                                            value={newMetricName}
                                            onChange={(id, value) => setNewMetricName(value)}
                                        />
                                    </div>
                                </div>

                                <div className='input-block d-flex flex-row justify-between'>
                                    <div className='input-description'>
                                        <div className='input-title'>Display name</div>
                                        <div className='small-font'>Human-readable name</div>
                                    </div>
                                    <div className='align-wrapper d-flex align-center'>
                                        <CustomInput
                                            id="new-metric-display-name"
                                            label="Display Name"
                                            placeholder="e.g., Customer Effort Score"
                                            type="text"
                                            value={newMetricDisplayName}
                                            onChange={(id, value) => setNewMetricDisplayName(value)}
                                        />
                                    </div>
                                </div>

                                <div className='input-block d-flex flex-row justify-between'>
                                    <div className='input-description'>
                                        <div className='input-title'>Metric type</div>
                                        <div className='small-font'>Type of value</div>
                                    </div>
                                    <div className='align-wrapper d-flex align-center'>
                                        <CustomSelect
                                            id="new-metric-type"
                                            label="Type"
                                            options={METRIC_TYPE_OPTIONS}
                                            value={newMetricType}
                                            multiSelect={false}
                                            onChange={(val) => {
                                                setNewMetricType(val);
                                                // Reset allowed values when type changes
                                                if (val !== "categorical" && val !== "list") {
                                                    setNewMetricAllowedValues([]);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {(newMetricType === "categorical" || newMetricType === "list") && (
                                    <div className='input-block d-flex flex-row justify-between'>
                                        <div className='input-description'>
                                            <div className='input-title'>Allowed Values</div>
                                            <div className='small-font'>
                                                {newMetricType === "categorical"
                                                    ? "Define the possible values for this categorical metric"
                                                    : "Define the possible items that can appear in this list"}
                                            </div>
                                        </div>
                                        <div className='align-wrapper d-flex flex-column'>
                                            {newMetricAllowedValues.map((value, index) => (
                                                <div key={index} className='d-flex align-center gap mb-2'>
                                                    <CustomInput
                                                        id={`allowed-value-${index}`}
                                                        label={`Value ${index + 1}`}
                                                        placeholder="Enter value"
                                                        type="text"
                                                        value={value}
                                                        onChange={(id, val) => {
                                                            const updated = [...newMetricAllowedValues];
                                                            updated[index] = val;
                                                            setNewMetricAllowedValues(updated);
                                                        }}
                                                    />
                                                    <Button
                                                        variant='danger'
                                                        size='small'
                                                        onClick={() => {
                                                            setNewMetricAllowedValues(
                                                                newMetricAllowedValues.filter((_, i) => i !== index)
                                                            );
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </Button>
                                                </div>
                                            ))}
                                            <Button
                                                variant='secondary'
                                                size='medium'
                                                onClick={() => setNewMetricAllowedValues([...newMetricAllowedValues, ""])}
                                            >
                                                <i className="fa-solid fa-plus"></i> Add Value
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className="input-block d-flex flex-row justify-between">
                                    <div className="input-description">
                                        <div className="input-title">Description</div>
                                        <div className="small-font">Optional description</div>
                                    </div>
                                    <div className="align-wrapper d-flex align-center">
                                        <CustomTextarea
                                            id="new-metric-description"
                                            name="description"
                                            type="text"
                                            addedText={newMetricDescription}
                                            onChange={(value) => setNewMetricDescription(value)}
                                            resizable={false}
                                            height="80px"
                                        />
                                    </div>
                                </div>

                                <div className="buttons-container justify-end gap-12">
                                    <Button background="color2" size="medium" onClick={onAddMetric}>
                                        Add Metric
                                    </Button>
                                    <Button variant="danger" size="medium" onClick={() => setShowNewMetricForm(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!isNew && (
              <div className="row mt-2">
                  <PostCallAnalyticsSummary
                    summary={summary}
                    loading={summaryLoading}
                    numericMetrics={numericMetrics}
                    categoricalMetrics={categoricalMetrics}
                    selectedPeriod={selectedSummaryPeriod}
                    onPeriodChange={handleSummaryPeriodChange}
                    customFrom={customSummaryFrom}
                    customTo={customSummaryTo}
                    setCustomFrom={setCustomSummaryFrom}
                    setCustomTo={setCustomSummaryTo}
                    onRefresh={fetchSummary}
                  />
              </div>
            )}

        </div>
    );
}

function PostCallAnalysisDetailWrapper({ canEdit = true }) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <PostCallAnalysisApiProvider>
                    <AgentApiProvider>
                        <ChannelApiProvider>
                            <PostCallAnalysisDetail canEdit={canEdit} />
                        </ChannelApiProvider>
                    </AgentApiProvider>
                </PostCallAnalysisApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default PostCallAnalysisDetailWrapper;
